export const BULK_DOWNLOAD_MAX_ROWS = 2000;
export const BULK_DOWNLOAD_FETCH_PAGE_SIZE = 100;
export const BULK_DOWNLOAD_FETCH_CONCURRENCY = 3;

export interface IBulkCollectProgress {
    fetched: number;
    total: number;
}

export interface IBulkCollectResult {
    rows: any[];
    failedPages: number[];
    cancelled: boolean;
}

/**
 * Fetches paged search results in parallel batches for a bulk download,
 * without touching the grid's redux state.
 *
 * - Pages are fetched in page order, `concurrency` at a time.
 * - Stops once `maxRows` have been collected.
 * - A failed page is recorded in `failedPages`; remaining pages continue.
 * - `isCancelled` is evaluated between batches; in-flight batches finish
 *   but their rows are discarded when cancelled.
 */
export const collectRowsForBulkDownload = async (
    totalCount: number,
    maxRows: number,
    concurrency: number,
    fetchPage: (pageNumber: number, pageSize: number) => Promise<any[]>,
    onProgress: (progress: IBulkCollectProgress) => void,
    isCancelled: () => boolean,
): Promise<IBulkCollectResult> => {
    const targetRows = Math.min(Math.max(totalCount, 0), maxRows);
    const result: IBulkCollectResult = { rows: [], failedPages: [], cancelled: false };

    if (targetRows === 0) {
        return result;
    }

    const pageSize = BULK_DOWNLOAD_FETCH_PAGE_SIZE;
    const pageCount = Math.ceil(targetRows / pageSize);
    const pages = Array.from({ length: pageCount }, (_, i) => i + 1);

    onProgress({ fetched: 0, total: targetRows });

    for (let i = 0; i < pages.length; i += concurrency) {
        if (isCancelled()) {
            result.cancelled = true;
            return result;
        }

        const batch = pages.slice(i, i + concurrency);
        const settled = await Promise.all(
            batch.map(async (page) => {
                try {
                    const rows = await fetchPage(page, pageSize);
                    return { page, rows };
                } catch {
                    return { page, rows: null };
                }
            })
        );

        if (isCancelled()) {
            result.cancelled = true;
            return result;
        }

        for (const { page, rows } of settled) {
            if (rows === null) {
                result.failedPages.push(page);
            } else {
                for (const row of rows) {
                    if (result.rows.length < targetRows) {
                        result.rows.push(row);
                    }
                }
            }
        }

        onProgress({ fetched: result.rows.length, total: targetRows });
    }

    return result;
};
